import React, { useState, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { Visibility } from '@material-ui/icons';
import { toast } from 'react-toastify';

import { columnDefs, defaultColDef } from './definitions-config';
import { getEnrollmentList } from '../../../../services/apiService';
import InformationModal from './_modals/InformationModal';

const PAGE_SIZE = 10;
const PRIMARY = '#5147ef';

function ActionCellRenderer(params) {
  const { onView, onEdit } = params.context;
  const canEdit = !params?.data?.rowComplete || params?.data?.enrollmentStatus === 'Pending';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%' }}>
      {canEdit && (
        <button
          onClick={() => onEdit(params.data)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 13, fontWeight: 700, color: PRIMARY,
            fontFamily: "'Gellix','Inter',sans-serif",
          }}
        >
          Edit
        </button>
      )}
      <Visibility
        onClick={() => onView(params.data)}
        style={{ fontSize: 16, color: '#555', cursor: 'pointer' }}
        titleAccess="View"
      />
    </div>
  );
}

const getServerSideDatasource = () => ({
  getRows: (params) => {
    const { startRow, endRow, filterModel } = params.request;

    // Convert startRow/endRow to page/limit
    const pageSize = endRow - startRow;
    const page = Math.floor(startRow / pageSize) + 1;

    // Build filter params from AG Grid filter model
    const filterParams = {};
    if (filterModel) {
      Object.entries(filterModel).forEach(([field, filterObj]) => {
        if (filterObj?.filter) {
          filterParams[field] = filterObj.filter;
        } else if (filterObj?.values) {
          filterParams[field] = filterObj.values.join(',');
        }
      });
    }

    const requestParams = {
      page,
      limit: pageSize,
      ...filterParams,
    };

    getEnrollmentList(requestParams)
      .then((res) => {
        params.successCallback(
          res?.rows || res?.data || [],
          res?.count || res?.pagination?.totalCount || 0
        );
      })
      .catch((err) => {
        console.error(err);
        params.failCallback();
        toast.error('Failed to fetch enrollment data');
      });
  },
});

export default function EnrolmentFinancialTab() {
  const gridRef = useRef();
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const refreshGridRef = useRef(null);

  const resolvedColumnDefs = columnDefs.map((col) =>
    col.cellRenderer === 'actionCellRenderer'
      ? { ...col, cellRenderer: ActionCellRenderer }
      : col
  );

  const onGridReady = useCallback((params) => {
    refreshGridRef.current = () => {
      setTimeout(() => {
        const datasource = getServerSideDatasource();
        params.api.setServerSideDatasource(datasource);
      }, 0);
    };
    refreshGridRef.current();
  }, []);

  const reloadData = () => {
    if (refreshGridRef.current) {
      refreshGridRef.current();
    }
  };

  return (
    <>
      <div
        style={{
          backgroundColor: 'var(--card-background, #fff)',
          borderRadius: 16,
          border: '1px solid var(--border-light, rgba(0,0,0,0.07))',
          boxShadow: 'var(--shadow-sm, 0 1px 6px rgba(0,0,0,0.05))',
          overflow: 'hidden',
        }}
      >
        <div className="ag-theme-alpine" style={{ width: '100%', height: 520 }}>
          <AgGridReact
            ref={gridRef}
            columnDefs={resolvedColumnDefs}
            defaultColDef={defaultColDef}
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            onGridReady={onGridReady}
            rowModelType="serverSide"
            serverSideStoreType="partial"
            animateRows
            serverSideInfiniteScroll
            pagination
            paginationPageSize={PAGE_SIZE}
            cacheBlockSize={PAGE_SIZE}
            context={{
              onView: setViewData,
              onEdit: setEditData,
              refreshGrid: reloadData,
            }}
          />
        </div>
      </div>

      {/* View-only modal */}
      <InformationModal
        open={!!viewData}
        onClose={() => setViewData(null)}
        data={viewData}
      />

      {/* Edit modal — reuses InformationModal in edit mode */}
      <InformationModal
        open={!!editData}
        onClose={() => { setEditData(null); reloadData(); }}
        data={editData}
        editable
      />
    </>
  );
}
